import { Injectable, NgZone } from "@angular/core";

/** Loudness of every tone (0–1). Kept low: the call audio plays through the same speakers. */
const VOLUME = 0.12;
/** North American ringback: 440 + 480 Hz, 2 s on, 4 s off. */
const RINGBACK_HZ = [440, 480];
const RING_ON_S = 2;
const RING_CYCLE_MS = 6000;
/** Hang-up cue: three short falling beeps, played once. */
const DISCONNECT_HZ = [620, 520, 420];
const BEEP_S = 0.16;
const BEEP_GAP_S = 0.06;

interface Ringback {
  gain: GainNode;
  oscillators: OscillatorNode[];
  timer: ReturnType<typeof setInterval>;
}

/**
 * Call progress tones, generated with Web Audio (no audio files). Browsers only allow audio
 * after a user gesture, so callers `prime()` inside the click that starts a call; until then,
 * or where Web Audio is missing, every method is a silent no-op.
 */
@Injectable({ providedIn: "root" })
export class CallToneService {
  private ctx: AudioContext | null = null;
  private ringback: Ringback | null = null;
  private beeps: OscillatorNode[] = [];

  constructor(private zone: NgZone) {}

  /** Creates or resumes the audio context. Call it from the click handler, before any await. */
  prime(): void {
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    try {
      this.ctx ??= new Ctor();
      if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => undefined);
    } catch {
      this.ctx = null;
    }
  }

  /** Starts the ringback loop; a second call while it rings does nothing. */
  startRingback(): void {
    const ctx = this.ctx;
    if (!ctx || this.ringback) return;
    this.stopBeeps();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    const oscillators = RINGBACK_HZ.map((hz) => {
      const osc = ctx.createOscillator();
      osc.frequency.value = hz;
      osc.connect(gain);
      osc.start();
      return osc;
    });
    const ring = () => {
      const t = ctx.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(VOLUME, t + 0.02);
      gain.gain.setValueAtTime(VOLUME, t + RING_ON_S);
      gain.gain.linearRampToValueAtTime(0, t + RING_ON_S + 0.02);
    };
    ring();
    // Outside Angular: the cadence must not run change detection every cycle.
    const timer = this.zone.runOutsideAngular(() => setInterval(ring, RING_CYCLE_MS));
    this.ringback = { gain, oscillators, timer };
  }

  stopRingback(): void {
    const ring = this.ringback;
    if (!ring) return;
    this.ringback = null;
    clearInterval(ring.timer);
    ring.gain.gain.cancelScheduledValues(0);
    ring.gain.gain.value = 0;
    for (const osc of ring.oscillators) {
      try {
        osc.stop();
      } catch {
        // Already stopped.
      }
      osc.disconnect();
    }
    ring.gain.disconnect();
  }

  /** The hang-up cue. Stops the ringback first, so the two never overlap. */
  playDisconnect(): void {
    const ctx = this.ctx;
    this.stopRingback();
    if (!ctx) return;
    this.stopBeeps();
    const t0 = ctx.currentTime + 0.05;
    DISCONNECT_HZ.forEach((hz, i) => {
      const start = t0 + i * (BEEP_S + BEEP_GAP_S);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = hz;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(VOLUME, start + 0.01);
      gain.gain.setValueAtTime(VOLUME, start + BEEP_S - 0.02);
      gain.gain.linearRampToValueAtTime(0, start + BEEP_S);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.onended = () => {
        osc.disconnect();
        gain.disconnect();
        this.beeps = this.beeps.filter((b) => b !== osc);
      };
      osc.start(start);
      osc.stop(start + BEEP_S + 0.01);
      this.beeps.push(osc);
    });
  }

  /** Silences everything at once: ringback and any hang-up cue still playing. */
  stopAll(): void {
    this.stopRingback();
    this.stopBeeps();
  }

  private stopBeeps(): void {
    for (const osc of this.beeps) {
      try {
        osc.stop();
      } catch {
        // Already stopped.
      }
    }
    this.beeps = [];
  }
}
